'use client'

/**
 * Find Your Fridge — the inner of the canvas `.quiz-card`, verbatim: the Back
 * button + "QUESTION 0N / 05" mono label, the percent label, the 3px orange
 * bar, five question steps of `.quiz-opt` buttons, then "Your three honest
 * matches" with the bucket's three real rows and the "Shop all" link, and
 * "↻ Start over". Buckets are real products (lib/homepage-2026 fetchQuiz); the
 * bucket logic is the artboard's quizVals().
 */
import Link from 'next/link'
import * as React from 'react'
import { SafeImage } from '@/components/ui/safe-image'
import type { QuizBucket } from '@/lib/homepage-2026/types'
import { recommendedCuFtFor } from '@/lib/refrigerator-answer-facts'
import { dealLinkName, formatPrice, homeDealCtaLabel } from './format'

const QUESTIONS: Array<{ q: string; sub: string; options: Array<[string, string]> }> = [
  // FRIDGE-1435: the household capacities are the size calculator's own
  // recommendations (lib/refrigerator-answer-facts.ts), so the quiz cannot give
  // a family of four a different number than the quick answer beside it.
  { q: "Who's eating from this fridge?", sub: "Household size helps choose a starting product group.", options: [['Just me (or two)', `${recommendedCuFtFor(1)}–${recommendedCuFtFor(2)} cu ft`], ['Family of 3–4', `${recommendedCuFtFor(3)}–${recommendedCuFtFor(4)} cu ft`], ['5 or more', `${recommendedCuFtFor(5)}+ cu ft`], ['We entertain a lot', '25+ cu ft']] },
  { q: 'What style fits your kitchen?', sub: 'Choose a style to help guide your starting group.', options: [['French door', 'Wide shelves, freezer below'], ['Side-by-side', 'Equal fridge & freezer'], ['Classic top or bottom freezer', 'Simple & efficient'], ['Compact or mini', 'Small-space cooling']] },
  { q: 'Where will it live?', sub: 'Location helps choose a group. Verify dimensions and clearance on the product page.', options: [['Standard kitchen', 'Full-depth is fine'], ['Flush, built-in look', 'Counter-depth'], ['Tight space or apartment', 'Slim widths'], ['Garage or second unit', 'Garage-ready models']] },
  { q: "What's the budget?", sub: 'Budget helps choose a group; individual prices may vary.', options: [['Under $500', 'Compact territory'], ['$500–$1,500', 'The value sweet spot'], ['$1,500–$2,500', 'Full-size, full-feature'], ['$2,500+', 'Premium picks']] },
  { q: 'What matters most?', sub: 'Keep this priority in mind while comparing. It does not change the product group.', options: [['Biggest capacity', 'Every cubic foot'], ['Energy & quiet', 'Low bills, low noise'], ['Smart features', 'Screens & WiFi'], ['Best value today', 'Deal-first picks']] },
]
const BUCKET_LABEL: Record<QuizBucket['key'], string> = {
  french: 'French door matches',
  counter: 'Counter-depth matches',
  compact: 'Compact & mini matches',
  garage: 'Garage-ready matches',
  value: 'Refrigerators under $1,000',
  sxs: 'Side-by-side matches',
}
const BUCKET_ALL: Record<QuizBucket['key'], string> = {
  french: 'Shop all french door refrigerators',
  counter: 'Shop all counter-depth refrigerators',
  compact: 'Shop all mini fridges',
  garage: 'Shop all garage-ready units',
  value: 'Shop refrigerators under $1,000',
  sxs: 'Shop all side-by-side refrigerators',
}

function bucketFor(a: Record<number, number>): QuizBucket['key'] {
  // the artboard's quizVals(): a3 = where it lives, a2 = style, a1 = household, a4 = budget
  if (a[3] === 2) return 'counter'
  if (a[2] === 4 || a[1] === 1 || a[4] === 1) return 'compact'
  if (a[3] === 4) return 'garage'
  if (a[4] === 2) return 'value'
  if (a[2] === 2) return 'sxs'
  return 'french'
}

const SEL: React.CSSProperties = { borderColor: '#ff9951', boxShadow: 'inset 0 0 0 1px #ff9951', background: 'rgba(255,153,81,0.06)' }

export function QuizCard({ buckets }: { buckets: QuizBucket[] }) {
  const [answers, setAnswers] = React.useState<Record<number, number>>({})
  const answered = Object.keys(answers).length
  const step = Math.min(answered + 1, 5)
  const done = answered >= 5
  const bucket = done ? buckets.find((b) => b.key === bucketFor(answers)) : null
  const headingRef = React.useRef<HTMLHeadingElement>(null)
  const previousAnswered = React.useRef(answered)
  React.useEffect(() => {
    if (previousAnswered.current !== answered) headingRef.current?.focus()
    previousAnswered.current = answered
  }, [answered])
  const back = () => {
    const last = [5, 4, 3, 2, 1].find((q) => answers[q])
    if (!last) return
    const next = { ...answers }
    delete next[last]
    setAnswers(next)
  }
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', paddingBottom: '8px' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}>
          <button type="button" onClick={back} style={{ visibility: answered > 0 ? 'visible' : 'hidden', fontSize: '11.5px', fontWeight: 700, color: '#6a7282', background: 'none', border: '1px solid #e5e7eb', borderRadius: '9999px', padding: '8px 16px', minHeight: '32px', cursor: 'pointer' }}>
            &larr; Back
          </button>
          <span className="quiz-mono" style={{ color: '#ff9951' }}>{done ? 'YOUR MATCHES' : `QUESTION 0${step} / 05`}</span>
        </span>
        <span style={{ fontSize: '10.5px', color: '#6a7282', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>{done ? '100% complete' : `${step * 20}% complete`}</span>
      </div>
      <div style={{ height: '3px', borderRadius: '9999px', background: '#e5e7eb', overflow: 'hidden', marginBottom: '16px' }}>
        <span style={{ display: 'block', height: '100%', background: '#ff9951', transition: 'width 0.4s ease', width: `${done ? 100 : step * 20}%` }} />
      </div>
      {!done ? (
        <div>
          <h3 ref={headingRef} tabIndex={-1} className="scroll-mt-24" style={{ margin: 0, fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', color: '#101828' }}>{QUESTIONS[step - 1].q}</h3>
          <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#4b5565' }}>{QUESTIONS[step - 1].sub}</p>
          <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: '10px' }}>
            {QUESTIONS[step - 1].options.map(([label, sub], i) => (
              <button key={label} type="button" className="quiz-opt" style={answers[step] === i + 1 ? SEL : undefined} onClick={() => setAnswers({ ...answers, [step]: i + 1 })}>
                <div style={{ fontSize: '14px', fontWeight: 800, letterSpacing: '-0.01em', color: '#101828', marginBottom: '2px' }}>{label}</div>
                <div className="quiz-mono" style={{ color: '#6a7282', letterSpacing: '0.02em', fontWeight: 500 }}>{sub}</div>
              </button>
            ))}
          </div>
        </div>
      ) : bucket && bucket.products.length > 0 ? (
        <div>
          <h3 ref={headingRef} tabIndex={-1} className="scroll-mt-24" style={{ margin: 0, fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', color: '#101828' }}>Explore this product group</h3>
          <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#4b5565' }}>Your household, style, location and budget answers choose a product group. These are examples from that group, with prices where available. Check dimensions and features on each product page.</p>
          <div style={{ marginTop: '14px' }}>
            {/* FRIDGE-1103 (owner: "Animate"): the bucket label, the match rows and the
                "Shop all" link fade up in sequence when the fifth answer lands. They
                mount fresh each time the quiz completes, so a one-shot CSS animation
                replays on every run with no JS and no state. The RESULT HEADING above
                is deliberately left alone: `headingRef` moves focus to it on the same
                render, and animating a freshly focused element fights screen readers
                and focus management. */}
            <div className="quiz-reslabel" style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#0469f0', marginBottom: '8px' }}>{BUCKET_LABEL[bucket.key]}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {bucket.products.map((p) => (
                <Link key={p.id} href={p.href} aria-label={dealLinkName(p)} className="quiz-resrow" style={{ display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px 12px', textDecoration: 'none', color: 'inherit' }}>
                  <span style={{ width: '56px', height: '56px', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SafeImage src={p.image} alt="" width={56} height={56} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#101828', lineHeight: 1.3 }}>
                      <span style={{ color: "#0469f0" }}>{p.brand}</span> {p.title} &middot; <span className="idchip">{p.fin}</span>
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: 900, color: '#101828' }}>
                      {formatPrice(p.priceCents)}{' '}
                      <span style={{ display: 'inline-flex', alignItems: 'center', background: '#ff9951', color: '#101828', fontSize: '11.5px', fontWeight: 700, padding: '3px 12px', borderRadius: '9999px', marginLeft: '8px' }}>{homeDealCtaLabel(p.priceCents)}</span>
                    </span>
                  </span>
                </Link>
              ))}
            </div>
            <Link href={bucket.href} className="quiz-resall" style={{ display: 'inline-block', marginTop: '10px', fontSize: '13px', fontWeight: 700 }}>
              {BUCKET_ALL[bucket.key]} &rarr;
            </Link>
          </div>
          <button type="button" onClick={() => setAnswers({})} style={{ marginTop: '14px', fontSize: '12.5px', fontWeight: 700, color: '#6a7282', background: 'none', border: '1px solid #e5e7eb', borderRadius: '9999px', padding: '8px 16px', cursor: 'pointer' }}>
            &#8635; Start over
          </button>
        </div>
      ) : (
        <div>
          <h3 ref={headingRef} tabIndex={-1} className="scroll-mt-24" style={{ margin: 0, fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', color: '#101828' }}>No product examples available</h3>
          <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#4b5565' }}>We could not load examples for the group selected by your answers. Try different answers or browse the catalog.</p>
          <Link href="/collections/refrigerators" style={{ display: 'inline-block', marginTop: 12, fontSize: 13, fontWeight: 700 }}>Browse refrigerators</Link>
          <button type="button" onClick={() => setAnswers({})} style={{ display: 'block', marginTop: 14, minHeight: 44, padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 9999, background: 'none', cursor: 'pointer' }}>Start over</button>
        </div>
      )}
    </>
  )
}
